import { createRef, useState } from 'react';

import { Box, RadioGroup, Text, TextInput, useMedia } from '@opendoor/bricks-next';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Sentry } from '@opendoor/observability';
import { push } from 'connected-react-router';

import AddressMapElement from 'src/components/fields/AddressMapElement';
import { BottomBar } from 'src/components/flowPage/twoColumn/BottomBar';
import FlowPageHeader from 'src/components/flowPage/twoColumn/FlowPageHeader';
import { ProgressIndicator } from 'src/components/flowPage/twoColumn/ProgressIndicator';
import NavigationLayout from 'src/components/layouts/NavigationLayout';
import { PageHeader, PageLayout } from 'src/components/layouts/PageLayout';
import {
  QuestionFieldContainer,
  QuestionFieldsContainer,
} from 'src/components/layouts/QuestionField';
import {
  Column,
  ColumnContainer,
  TwoColumnPageContent,
} from 'src/components/layouts/TwoColumnPageLayout';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import { extractStreetNumber } from 'src/helpers/contextHelpers';
import { sellerFlowPagePath } from 'src/routes/pathBuilders';
import { api } from 'src/store/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getInApp } from 'src/store/router/selectors';
import { TO_MANUAL_ADDRESS_VERIFICATION } from 'src/store/sellerFlow/actionTypes';
import { maybeSetLeadCookie } from 'src/store/sellerFlow/saga';
import {
  address as addressSelector,
  isAddressUnitNumberMissing as getIsAddressUnitNumberMissing,
  isAddressUnitNumberWrong as getIsAddressUnitNumberWrong,
  leadToken as leadTokenSelector,
} from 'src/store/sellerFlow/selectors';
import { changeAnswers } from 'src/store/sellerInput';

import { toUpdatedAddress } from './ManualAddressEntryPage';

const AddressConfirmationPage: React.FC = () => {
  const inApp = useAppSelector(getInApp);
  const sellerInputUUID = useAppSelector(leadTokenSelector);
  const dispatch = useAppDispatch();
  const address = useAppSelector(addressSelector);
  const isAddressUnitNumberMissing = useAppSelector(getIsAddressUnitNumberMissing);
  const isAddressUnitNumberWrong = useAppSelector(getIsAddressUnitNumberWrong);
  const [modifyAddress, { isLoading: isModifyLoading, error: modifyError, data: modifyData }] =
    api.useModifyAddressMutation();
  const [verifyAddress, { isLoading: isVerifyLoading, error: verifyError }] =
    api.useVerifyAddressMutation();

  const titleRef = createRef<HTMLHeadingElement>();
  const [unitNumber, setUnitNumber] = useState<string>('');
  const streetNumber = extractStreetNumber(address);
  let errorMessage = '';
  const [forceVerify, setForceVerify] = useState(false);

  const navigateToCoreFlow = (sellerInputUUID) => {
    dispatch(changeAnswers([{ key: 'home.user_verified_address', answer: true }]));
    dispatch(
      push(
        sellerFlowPagePath({
          token: sellerInputUUID,
          slug: '',
          inApp,
        }),
      ),
    );
  };

  const handleNextButtonClick = () => {
    modifyAddress({
      sellerInputUUID,
      // only overwrite the unitNumber if the input is not blank
      address: unitNumber
        ? toUpdatedAddress({ ...address, unitNumber })
        : toUpdatedAddress(address),
    });
  };
  const { smallerThanMD } = useMedia();

  const radioGroupOptions = [
    {
      label: 'No unit number',
      value: 'no_unit_number',
    },
  ];

  let radioGroupTitle = 'Does this apply to your address?';
  if (unitNumber === streetNumber) {
    radioGroupOptions.push({
      label: 'Same street and unit number',
      value: unitNumber,
    });
    radioGroupTitle = 'Do either of these apply to your address?';
    errorMessage = `That looks like a street number. If you don’t have a unit number, select “no unit number".`;
  }

  if (modifyData && !isModifyLoading && !modifyError && !forceVerify) {
    if (isAddressUnitNumberMissing || isAddressUnitNumberWrong) {
      errorMessage = 'Unit number not found or entered incorrectly';
    } else {
      navigateToCoreFlow(sellerInputUUID);
    }
  }

  if (modifyError || verifyError) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        error: modifyError || verifyError,
        address,
        unitNumber,
        sellerInputUUID,
      });
      Sentry.captureMessage('MissingUnitNumber: GraphQL error');
    });
    showToast(
      'error',
      'Oops, something went wrong! Please try again.',
      'reception-fe-missing-unit-number-gql-error-toast',
      {
        toastOptions: { toastId: 'error-toast', autoClose: 7000 },
      },
    );
  }

  const updateUnitNumberAndVerify = (unitNumber: undefined | string) => {
    if (unitNumber === 'no_unit_number') {
      setForceVerify(true);
      // Force verified status on address.
      verifyAddress({
        sellerInputUUID,
        address,
      }).then(() => {
        maybeSetLeadCookie({ token: sellerInputUUID });
        navigateToCoreFlow(sellerInputUUID);
      });
    } else {
      // Update address
      modifyAddress({
        sellerInputUUID,
        address: toUpdatedAddress({ ...address, unitNumber }),
      }).then(() => {
        setForceVerify(true);
        // Force verified status on address.
        verifyAddress({
          sellerInputUUID,
          address,
        }).then(() => {
          maybeSetLeadCookie({ token: sellerInputUUID });
          navigateToCoreFlow(sellerInputUUID);
        });
      });
    }
  };

  return (
    <PageLayout>
      <PageHeader />
      <TwoColumnPageContent>
        <ColumnContainer>
          <Column>
            <Box flexDirection="column" gap={20} $smallerThanMD={{ gap: 12 }}>
              <FlowPageHeader title="Is this the right address?" ref={titleRef} />
              <AddressMapElement />
              <Text
                mt={10}
                typography="$subheaderSmall"
                tag="h3"
                fontWeight="500"
                $smallerThanMD={{ typography: '$bodyMedium', fontWeight: '500' }}
              >
                {`${address.street} ${
                  address.street.includes('#') ? '' : `${unitNumber ? '#' : ''}${unitNumber}`
                }\n${address.city}, ${address.state} ${address.postalCode}`}
              </Text>
            </Box>
          </Column>
          <Column>
            {(isAddressUnitNumberMissing || isAddressUnitNumberWrong) && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleNextButtonClick();
                }}
              >
                <QuestionFieldsContainer>
                  <QuestionFieldContainer>
                    <Box flexDirection="column" gap={30} $smallerThanMD={{ gap: 15 }}>
                      <TextInput
                        title="Add apt, suite, unit, etc."
                        errorText={errorMessage}
                        disabled={isModifyLoading || isVerifyLoading}
                        inputMode="text"
                        id="unit_number"
                        name="unit_number"
                        placeholder="Unit #1234"
                        value={unitNumber}
                        onChangeText={(text: string) => setUnitNumber(text)}
                        size={smallerThanMD ? 'medium' : 'large'}
                      />
                      <Box py={smallerThanMD ? 7 : null}></Box>
                      <RadioGroup
                        title={radioGroupTitle}
                        disabled={isModifyLoading || isVerifyLoading}
                        id="unit_misc"
                        size={smallerThanMD ? 'medium' : 'large'}
                        onPress={updateUnitNumberAndVerify}
                        analyticsName="reception-fe-unit-misc"
                        options={radioGroupOptions}
                      />
                    </Box>
                  </QuestionFieldContainer>
                </QuestionFieldsContainer>
              </form>
            )}
          </Column>
        </ColumnContainer>
        <BottomBar>
          <ProgressIndicator />
          <NavigationLayout
            nextButton={{
              buttonText: 'Next',
              loading: isModifyLoading,
              onClickHandler: handleNextButtonClick,
            }}
            skipButton={{
              buttonText: 'Edit',
              loading: false,
              onClickHandler: () => dispatch({ type: TO_MANUAL_ADDRESS_VERIFICATION }),
            }}
          />
        </BottomBar>
      </TwoColumnPageContent>
    </PageLayout>
  );
};

const AddressConfirmationPageWithLoader = () => (
  <ReceptionLoader>
    <AddressConfirmationPage />
  </ReceptionLoader>
);

export default AddressConfirmationPageWithLoader;
